import { useState, useCallback } from 'react';
import * as api from '../services/api';

/**
 * Hook for managing subscription tiers (admin only)
 */
export function useTiers() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);

  /**
   * Fetch tiers with optional filters
   */
  const fetchTiers = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);

      const result = await api.fetchTiers(options);
      
      setTiers(result.tiers || []);
      setPagination(result.pagination || null);

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new tier
   */
  const createTier = useCallback(async (tierData) => {
    try {
      setError(null);
      const result = await api.createTier(tierData);
      
      // Refresh the tier list
      await fetchTiers();
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchTiers]);

  /**
   * Update an existing tier
   */
  const updateTier = useCallback(async (tierId, tierData) => {
    try {
      setError(null);
      const result = await api.updateTier(tierId, tierData);

      // API may return the tier object directly or wrap it as { tier }
      const updatedTier = result?.tier ?? result;

      // Update the tier in state
      setTiers((prev) => prev.map((tier) => (tier.id === tierId ? updatedTier : tier)));

      return updatedTier;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update tier status (active/inactive)
   */
  const updateTierStatus = useCallback(async (tierId, status) => {
    try {
      setError(null);
      const result = await api.updateTierStatus(tierId, status);
      
      // Update the tier in state
      setTiers((prev) => 
        prev.map((tier) => (tier.id === tierId ? result.tier : tier))
      );
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update tier rank (display order)
   */
  const updateTierRank = useCallback(async (tierId, rank) => {
    try {
      setError(null);
      const result = await api.updateTierRank(tierId, rank);
      
      // Refresh the tier list to get updated ranks
      await fetchTiers();
      
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [fetchTiers]);

  /**
   * Delete a tier
   */
  const deleteTier = useCallback(async (tierId, migrateTo = null) => {
    try {
      setError(null);
      await api.deleteTier(tierId, migrateTo);
      
      // Remove the tier from state
      setTiers((prev) => prev.filter((tier) => tier.id !== tierId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    tiers,
    loading,
    error,
    pagination,
    fetchTiers,
    createTier,
    updateTier,
    updateTierStatus,
    updateTierRank,
    deleteTier,
  };
}
